import { Combobox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import { Fragment, useState } from 'react'

export interface ddlListItem {
    id: any
    name: string
    code?: string
}

export interface IDropDownList {
    list: ddlListItem[]
    className?: string
    width?: string
    height?: string
    minWidth?: string
    label?: string
    inputID: string
    onChange: (name: string, value: ddlListItem) => void
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
    selectedItem: ddlListItem
    name: string
    required: boolean
    error?: string
    isValid?: boolean
    isFocus?: boolean
    isBlur?: boolean
    disabled?: boolean
}

const DropDownList: React.FC<IDropDownList> = ({
    list,
    className,
    width,
    height,
    minWidth,
    label,
    onChange,
    onBlur,
    onFocus,
    inputID,
    selectedItem,
    name,
    required,
    error,
    isValid,
    isFocus,
    isBlur,
    disabled
}) => {
    const [query, setQuery] = useState('')

    const filteredItems =
        query === ''
            ? list
            : list.filter((listItem) =>
                listItem.name
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .includes(query.toLowerCase().replace(/\s+/g, ''))
            )

    return (
        <div className={classNames(className, width, required ? "h-[6.5rem]" : "")}>
            <Combobox
                value={selectedItem}
                onChange={(value: ddlListItem) => onChange(name, value)}
                name={name}
                nullable
                disabled={disabled}
            >
                {({ open }) => (
                    <div>
                        <div className="relative  ">
                            <label
                                htmlFor={inputID}
                                className={classNames(
                                    `font-sans block mb-1 text-md font-medium text-gray-700`
                                )}
                            >
                                {label}
                            </label>
                            <div className={classNames("relative w-full cursor-default overflow-hidden bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-[#9ccb45] text-sm border-[1.5px] border-gray-400",
                                height ? height : 'h-10',
                            )}
                            >
                                <Combobox.Input
                                    className={classNames(
                                        'w-full border-none pl-2 pr-7 md:text-sm text-xs leading-5 text-gray-900 focus:ring-0 ',
                                        height ? height : 'h-10',
                                        minWidth ? minWidth : 'min-w-[14rem]'
                                    )}
                                    displayValue={(listItem: ddlListItem) =>
                                        listItem?.name
                                    }
                                    onChange={(event) =>
                                        setQuery(event.target.value)
                                    }
                                    onBlur={onBlur}
                                    onFocus={onFocus}
                                    id={inputID}
                                    name={name}
                                    autoComplete="off"
                                    readOnly={true}
                                />

                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2" aria-label="DropdownButton">
                                    <ChevronDownIcon
                                        className="h-5 w-5 text-gray-400"
                                        aria-hidden="true"
                                    />
                                </Combobox.Button>
                            </div>
                            <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setQuery('')}
                            >
                                <Combobox.Options className="absolute z-40 mt-1 max-h-60 w-full overflow-auto bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none md:text-sm text-xs">
                                    {filteredItems.length === 0 &&
                                        query !== '' ? (
                                        <div className="relative z-40 cursor-default select-none py-2 px-4 text-gray-700">
                                            Nothing found.
                                        </div>
                                    ) : (
                                        filteredItems.map((listItem) => (
                                            <Combobox.Option
                                                key={listItem.id}
                                                className={({ active }) =>
                                                    `relative cursor-default select-none py-2 md:pl-8 pl-6 pr-4 z-40 ${active
                                                        ? 'bg-[#9ccb45] text-gray-900'
                                                        : 'text-gray-900'
                                                    }`
                                                }
                                                value={listItem}
                                            >
                                                {({ selected, active }) => (
                                                    <>
                                                        <span
                                                            className={`block truncate z-40 ${selected
                                                                ? 'font-medium'
                                                                : 'font-normal'
                                                                }`}
                                                        >
                                                            {listItem.name}
                                                        </span>
                                                        {selected ? (
                                                            <span
                                                                className={`absolute inset-y-0 left-0 flex items-center md:pl-2 pl-1 ${active
                                                                    ? 'text-white'
                                                                    : 'text-[#9ccb45]'
                                                                    }`}
                                                            >
                                                                <CheckIcon
                                                                    className="h-4 w-4"
                                                                    aria-hidden="true"
                                                                />
                                                            </span>
                                                        ) : null}
                                                    </>
                                                )}
                                            </Combobox.Option>
                                        ))
                                    )}
                                </Combobox.Options>
                            </Transition>
                        </div>
                        <div>
                            {isBlur && !isValid && !open && (
                                <p className="text-red-600 font-semibold pt-1">
                                    *{error}
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </Combobox>
        </div>
    )
}

export default DropDownList
